import { useState } from "react";

import { isEmpty, map, shuffle } from "lodash/fp";

import {
  Alert as MuiAlert,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import { SettingsModuleCard } from "@app/analytics/account/components/styles";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import { useAuthenticated } from "@app/common/auth/useAuth";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import { formattedListWithConjunction, pluralize } from "@app/common/utils";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import {
  isWorkspaceOwner,
  teamsWhereUserIsTheOnlyManager,
} from "@app/common/workspaces/workspaceUtils";
import Tracker from "@app/utils/tracking/Tracker";

import Button from "@rui/Button";
import ConfirmationModal from "@rui/ConfirmationModal";
import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import { Link } from "@rui/Link";
import Stack from "@rui/Stack";
import TextField from "@rui/TextField";

const COMMENT_CHARACTER_LIMIT = 255;

const Alert = styled(MuiAlert)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  color: theme.palette.shades.neutral[600],
}));

const DELETE_REASONS_CHOICES = [
  {
    value: "too_many_meetings",
    label: "Joining too many meetings",
  },
  {
    value: "difficult_to_use",
    label: "Difficult to use",
  },
  {
    value: "switching_product",
    label: "Switching to another product",
  },
  {
    value: "too_expensive",
    label: "Too expensive",
  },
  {
    value: "not_allowed",
    label: "My organization does not allow Read",
  },
  {
    value: "privacy_security",
    label: "Privacy / security",
  },
];

const OTHER_REASON = {
  value: "other",
  label: "Other",
};

// randomize to avoid bias
const DELETE_REASONS = [...shuffle(DELETE_REASONS_CHOICES), OTHER_REASON];

const DeleteAccount = () => {
  const [showDeleteError, setShowDeleteError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [additionalComments, setAdditionalComments] = useState("");

  const { deleteAccount, user } = useAuthenticated();
  const { workspace } = useWorkspace();
  const { userSettings } = useUserSettings();

  const handleDeleteAccountClick = () => setModalOpen(true);

  const closeModal = () => {
    setModalOpen(false);
    setShowDeleteError(false);
    setDeleteReason("");
    setAdditionalComments("");
  };

  const handleDeleteAccount = () => {
    setShowDeleteError(false);
    deleteAccount.mutate(
      { reason: deleteReason, comments: additionalComments },
      {
        onSuccess: () => {
          Tracker.setPeopleProperties({
            plan: "none",
            plan_cadence: "none",
          });
        },
        onError: () => setShowDeleteError(true),
      }
    );
  };

  const handleSelectDeleteReason = (event: SelectChangeEvent) => {
    setDeleteReason(event.target.value);
  };

  const isOwner = isWorkspaceOwner(user.workspace);
  const teamsRequiringNewManager = teamsWhereUserIsTheOnlyManager(
    workspace,
    user.id
  );

  const showAutoJoinPreferenceAlert =
    userSettings.calendarPermissionLevel !== "invite_only";
  const accountDeleteDisabled = isOwner || !isEmpty(teamsRequiringNewManager);
  const remainingCommentCharacters =
    COMMENT_CHARACTER_LIMIT - additionalComments.length;

  return (
    <>
      <SettingsModuleCard sx={{ padding: 3 }} elevation={0}>
        <Stack spacing={2}>
          {showAutoJoinPreferenceAlert && (
            <Alert severity="info">
              <Typography variant="bodySecondaryReg">
                Just want Read to join fewer meetings? Set your{" "}
                <Link
                  to={ROUTES.USER_SETTINGS_JOIN_PREFERENCES}
                  onClick={closeModal}
                >
                  auto-join preference
                </Link>{" "}
                to "Manually select meetings Read joins".
              </Typography>
            </Alert>
          )}
          {isOwner && (
            <Alert severity="warning">
              <Typography variant="bodySecondaryReg">
                You are the owner of the {workspace.name} Workspace. To delete
                your account, you must first{" "}
                <Link to={ROUTES.TRANSFER_WORKSPACE}>transfer ownership</Link>{" "}
                or{" "}
                <Link to={ROUTES.workspaceSettingsSettingsById(workspace.id)}>
                  delete the Workspace
                </Link>
                .
              </Typography>
            </Alert>
          )}
          {!isEmpty(teamsRequiringNewManager) && (
            <Alert severity="warning">
              <Typography variant="bodySecondaryReg">
                You are the only manager of{" "}
                {formattedListWithConjunction(
                  map("name", teamsRequiringNewManager)
                )}
                . To delete your account, you must first{" "}
                <Link to={ROUTES.workspaceSettingsById(workspace.id)}>
                  assign {pluralize("a", "", teamsRequiringNewManager.length)}{" "}
                  new {pluralize("manager", teamsRequiringNewManager.length)}
                </Link>
                .
              </Typography>
            </Alert>
          )}
          <Stack variant="center">
            <Button
              data-test-id="delete-account"
              variant="contained"
              color="error"
              disabled={accountDeleteDisabled}
              onClick={handleDeleteAccountClick}
              sx={{ borderRadius: "4px" }}
            >
              Delete my account
            </Button>
          </Stack>
        </Stack>
      </SettingsModuleCard>
      <ConfirmationModal
        open={modalOpen}
        onClose={closeModal}
        onConfirm={handleDeleteAccount}
        title="We're sorry to see you go"
        cancelProps={{ label: "Nevermind, keep account" }}
        confirmProps={{
          disabled: accountDeleteDisabled,
          label: "Delete account",
          testId: "confirm-delete-account",
          type: "negative",
        }}
        inverse
        loading={deleteAccount.isPending}
        sx={{ color: "shades.neutral.600" }}
        size="md"
        showHeaderCloseButton
      >
        <Stack spacing={1}>
          <Dropdown
            name="delete-reason"
            label="Please let us know the reason you are leaving"
            sxLabel={{
              textTransform: "none",
              color: "shades.neutral.700",
            }}
            placeholder="Select option"
            selectedItem={getSelectedFromOptions(DELETE_REASONS, deleteReason)}
            onChange={handleSelectDeleteReason}
          >
            {DELETE_REASONS.map(({ value, label }) => (
              <DropdownMenuItem key={value} value={value} label={label} />
            ))}
          </Dropdown>
          <TextField
            multiline
            rows={3}
            inputProps={{ maxLength: 255 }}
            placeholder="Anything you want to share? (Optional)"
            sx={{
              "& .MuiInputBase-root": { px: 1.5, py: 1 },
              "& textarea": {
                padding: 0,
                fontSize: "0.875rem",
                lineHeight: 1.5,
              },
            }}
            onChange={(e) => setAdditionalComments(e.target.value)}
            helperText={
              remainingCommentCharacters < COMMENT_CHARACTER_LIMIT
                ? `${remainingCommentCharacters} characters remaining`
                : undefined
            }
          />
          <Alert severity="error">
            This deletion is final and there is no way to recover your account
            or report data if you choose to continue.
          </Alert>
          {showDeleteError && (
            <Alert severity="error">
              <span>
                Sorry, your account could not be automatically deleted. Please
                try again or reach out to{" "}
                <Link href={LINKS.EMAIL.SUPPORT}>support@read.ai</Link> if the
                problem persists.
              </span>
            </Alert>
          )}
        </Stack>
      </ConfirmationModal>
    </>
  );
};

export default DeleteAccount;
